import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Link as LinkIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import config from '../config';
import ConfirmNewExpenseDialog, { PendingInvoice } from './ConfirmNewExpenseDialog';

interface OwnerOption {
  id: string;
  name: string;
}

interface BulkInvoiceReviewDialogProps {
  open: boolean;
  invoices: PendingInvoice[];
  /** Called whenever one invoice is resolved (linked or confirmed individually), and again with
   * every remaining id after a bulk action — the parent removes these from its own pending queue. */
  onResolved: (ids: string[]) => void;
  onClose: () => void;
}

/**
 * Shown instead of ConfirmNewExpenseDialog's one-at-a-time flow when several receipts/invoices
 * were uploaded in the same batch — a single page listing each document's headline info (title +
 * total) with its own "Add to transaction"/"Add as new" choice, plus "Add all as new" and "I'll
 * do it later" to clear the whole batch at once. Mirrors the certificates bulk-upload summary's
 * shape, adapted for invoices/receipts.
 */
const BulkInvoiceReviewDialog: React.FC<BulkInvoiceReviewDialogProps> = ({ open, invoices, onResolved, onClose }) => {
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [linkingInvoice, setLinkingInvoice] = useState<PendingInvoice | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [owners, setOwners] = useState<OwnerOption[]>([]);
  // '' means "no profile" (unscoped) — the global picker applies this to every not-yet-overridden
  // row's own selector below, and is what "Add all as new" uses for the whole batch at once.
  const [globalOwnerId, setGlobalOwnerId] = useState('');
  const [rowOwnerIds, setRowOwnerIds] = useState<Record<string, string>>({});

  // Reset local "already handled" tracking whenever a fresh batch opens.
  useEffect(() => {
    if (open) {
      setResolvedIds(new Set());
      setError(null);
      setGlobalOwnerId('');
      setRowOwnerIds({});
    }
  }, [open]);

  const token = localStorage.getItem('auth_token');
  const headers = () => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

  // Owners ("Profiles") list — who each invoice/receipt should be scoped to, same entity list
  // used everywhere else (Accounting's entity selector, PeoplePage). Fetched once per open.
  useEffect(() => {
    if (!open) return;
    fetch(`${config.apiUrl}/api/accounting/owners`, { headers: headers() })
      .then(res => res.ok ? res.json() : { owners: [] })
      .then(d => setOwners(d.owners || []))
      .catch(() => setOwners([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const remaining = invoices.filter(inv => !resolvedIds.has(inv.id));

  const markResolved = (id: string) => {
    setResolvedIds(prev => new Set(prev).add(id));
    onResolved([id]);
  };

  // Changing the global picker re-applies it to every row that hasn't been individually
  // overridden yet — rows the user already changed by hand keep their own choice.
  const handleGlobalOwnerChange = (ownerId: string) => {
    setGlobalOwnerId(ownerId);
  };
  const rowOwnerId = (invoiceId: string) => rowOwnerIds[invoiceId] ?? globalOwnerId;

  const handleAddAsNew = async (invoice: PendingInvoice) => {
    setConfirmingId(invoice.id);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/confirm`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ owner_id: rowOwnerId(invoice.id) || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to confirm expense');
      }
      markResolved(invoice.id);
    } catch (e: any) {
      setError(e.message || 'Failed to confirm expense');
    } finally {
      setConfirmingId(null);
    }
  };

  // excludeFromTransactions distinguishes "Add all as new" (these ARE real transactions, shows
  // up in the bank "View transactions" list) from "I'll do it later" (just wants them off the
  // pending queue without deciding anything — lands as an ordinary unlinked invoice/receipt,
  // stays out of the transactions list until explicitly added there later). Both apply
  // globalOwnerId to the whole batch — assigning different profiles per document instead is done
  // one at a time via "Add as new"'s own per-row picker above.
  const handleBulkConfirm = async (excludeFromTransactions: boolean) => {
    const ids = remaining.map(inv => inv.id);
    if (ids.length === 0) { onClose(); return; }
    setBulkLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/bulk-confirm`, {
        method: 'POST', headers: headers(), body: JSON.stringify({
          invoice_ids: ids, exclude_from_transactions: excludeFromTransactions, owner_id: globalOwnerId || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to confirm expenses');
      }
      onResolved(ids);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to confirm expenses');
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open && !linkingInvoice} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Review new invoices &amp; receipts ({remaining.length})</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These aren't counted in your totals yet. For each one, link it to an existing bank transaction
            it's proof of, or add it as its own new transaction — or handle the whole batch at once below.
          </Typography>

          {remaining.length > 0 && owners.length > 0 && (
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Assign all to profile</InputLabel>
              <Select
                label="Assign all to profile" value={globalOwnerId}
                onChange={e => handleGlobalOwnerChange(e.target.value)}
              >
                <MenuItem value=""><em>No profile</em></MenuItem>
                {owners.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {remaining.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3 }}>
              <CheckIcon color="success" sx={{ fontSize: 40 }} />
              <Typography variant="body2" color="text.secondary">All done!</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {remaining.map((invoice) => (
                <Paper key={invoice.id} variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                        {invoice.contractor_name || 'Untitled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {invoice.invoice_date} {invoice.document_type ? `· ${invoice.document_type}` : ''}
                      </Typography>
                    </Box>
                    <Chip label={`£${invoice.total}`} size="small" sx={{ fontWeight: 600 }} />
                    <Button
                      size="small" variant="outlined" startIcon={<LinkIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => setLinkingInvoice(invoice)}
                      disabled={confirmingId === invoice.id || bulkLoading}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Add to transaction
                    </Button>
                    <Button
                      size="small" variant="contained" startIcon={confirmingId === invoice.id ? <CircularProgress size={14} /> : <AddIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => handleAddAsNew(invoice)}
                      disabled={confirmingId === invoice.id || bulkLoading}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Add as new
                    </Button>
                  </Box>
                  {owners.length > 0 && (
                    <FormControl size="small" sx={{ maxWidth: 220 }}>
                      <InputLabel>Profile</InputLabel>
                      <Select
                        label="Profile" value={rowOwnerId(invoice.id)}
                        onChange={e => setRowOwnerIds(prev => ({ ...prev, [invoice.id]: e.target.value }))}
                        disabled={confirmingId === invoice.id || bulkLoading}
                      >
                        <MenuItem value=""><em>No profile</em></MenuItem>
                        {owners.map(o => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {remaining.length === 0 ? (
            <Button onClick={onClose} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={() => handleBulkConfirm(true)} disabled={bulkLoading} color="inherit" sx={{ mr: 'auto' }}>
                I'll do it later
              </Button>
              <Button
                onClick={() => handleBulkConfirm(false)} variant="contained" disabled={bulkLoading}
                startIcon={bulkLoading ? <CircularProgress size={16} /> : <AddIcon />}
              >
                Add all as new
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmNewExpenseDialog
        open={!!linkingInvoice}
        invoice={linkingInvoice}
        initialMode="link"
        onDismiss={() => setLinkingInvoice(null)}
        onResolved={() => {
          if (linkingInvoice) markResolved(linkingInvoice.id);
          setLinkingInvoice(null);
        }}
      />
    </>
  );
};

export default BulkInvoiceReviewDialog;
